import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/axios";
import { JalurGroup } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa6";
import { MdModeEdit } from "react-icons/md";
import { RiDeleteBack2Line } from "react-icons/ri";

const postData = async (newData: { nama_jalur_grup: string }) => {
  const response = await api.post("/master/jalur-grup/store", newData);
  return response.data;
};

const patchData = async (newData: {
  id_ref_jalur_grup: string;
  nama_jalur_grup: string;
}) => {
  const response = await api.patch("/master/jalur-grup/update", newData);
  return response.data;
};

const deleteData = async (deletedData: { id_ref_jalur_grup: string }) => {
  const response = await api({
    url: "/master/jalur-grup/delete",
    method: "DELETE",
    data: deletedData,
  });
  return response.data;
};

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE";
  selectedRow?: JalurGroup | null;
  closeModal: () => void;
}) {
  const [form, setForm] = useState({
    id_ref_jalur_grup: selectedRow?.id_ref_jalur_grup || "",
    nama_jalur_grup: selectedRow?.nama_jalur_grup || "",
  });

  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJalurGroup"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jalur"
          description={`Apakah anda yakin akan menghapus data jalur group "${selectedRow?.nama_jalur_grup}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Jalur Group" : "Update Data Jalur Group"}
      />
      <div>
        <Label>Nama Jalur Group</Label>
        <Input
          placeholder="Ketik nama jalur..."
          value={form.nama_jalur_grup}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              nama_jalur_grup: event.target.value,
            }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  );
}
